import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Brain, MessageSquare, Settings, Moon, Sun, Zap, Database, Globe, LogIn, LogOut, User, Crown, Phone, LayoutGrid, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { OFFER } from '../constants/demos';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, setTheme] = React.useState('dark');
  const { user, profile, signOut, loading } = useAuth();

  const isSelfServicePage = [
    '/auth', '/features', '/website-generator', '/upgrade',
    '/chat', '/automation', '/models', '/api-settings', '/admin'
  ].some(path => location.pathname === path || location.pathname.startsWith(`${path}/`));

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/examples', label: 'Examples', icon: LayoutGrid },
    { path: '/order', label: 'Order', fullLabel: `Order — $${OFFER.price}, live in 48 hours`, icon: Rocket, live: true },
    ...(isSelfServicePage ? [
    { path: '/features', label: 'Tools', fullLabel: 'Self-service tools' },
    { path: '/website-generator', label: 'Builder', fullLabel: 'Self-service website builder', icon: Globe, live: true },
    { path: '/chat', label: 'Chat', icon: MessageSquare, soon: true },
    { path: '/automation', label: 'Automation', icon: Zap, soon: true },
    { path: '/models', label: 'Models', icon: Database, soon: true },
    { path: '/api-settings', label: 'API', fullLabel: 'API Settings', icon: Settings, soon: true }
    ] : [])
  ];

  const adminNavItems = [
    { path: '/admin', label: 'Admin Dashboard', icon: Settings }
  ];

  const handleAuthAction = () => {
    if (user) {
      signOut();
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Beta announcement banner */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs py-1.5 text-center px-4">
        🚀 <span className="font-semibold">Founding rate: first {OFFER.foundingSpots} websites at ${OFFER.price} one-time</span> (then ${OFFER.anchorPrice}) — built &amp; live in 48 hours, no monthly fees.
        <span className="ml-2 opacity-75">Questions? Ask Mr. Support ↙</span>
      </div>
    <nav className="bg-gray-900/80 backdrop-blur-md border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-3 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Logo - Compact */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-shrink-0">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:flex flex-col">
              <span className="text-sm font-bold text-white leading-tight">globalnow.biz</span>
              <span className="text-xs text-gray-400 leading-tight">Business AI</span>
            </div>
          </Link>

          {/* Navigation Links - Compact */}
          <div className="flex items-center gap-2 lg:gap-4 flex-1 justify-center">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-xs lg:text-sm font-medium transition-colors hover:text-white flex items-center gap-1 relative ${
                  location.pathname === item.path ? 'text-white' : 'text-gray-400'
                }`}
                title={item.fullLabel || item.label}
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                <span className="hidden lg:inline">{item.label}</span>
                {item.live && (
                  <span className="hidden lg:inline text-xs bg-green-500/20 text-green-400 px-1 rounded">LIVE</span>
                )}
                {item.soon && (
                  <span className="hidden lg:inline text-xs bg-yellow-500/20 text-yellow-500 px-1 rounded">Soon</span>
                )}
              </Link>
            ))}
            
            {/* Admin Links (only for admins) */}
            {profile?.is_admin && adminNavItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-xs font-medium transition-colors hover:text-yellow-400 flex items-center gap-1 ${
                  location.pathname === item.path
                    ? 'text-yellow-400'
                    : 'text-yellow-500/70'
                }`}
                title={item.label}
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                <span className="hidden lg:inline">Admin</span>
              </Link>
            ))}
          </div>

          {/* Right Side - User & Auth */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Phone — always visible, top right */}
            <a
              href={`tel:${OFFER.phoneTel}`}
              className="flex items-center gap-1.5 text-xs lg:text-sm text-gray-300 hover:text-white transition-colors whitespace-nowrap"
              title={`Call us: ${OFFER.phoneDisplay} — answered 24/7`}
            >
              <Phone className="w-4 h-4 text-green-400" />
              <span className="hidden md:inline">Call us: <span className="font-semibold">{OFFER.phoneDisplay}</span></span>
            </a>
            {!loading && (
              <>
                {user && profile && (
                  <div className="hidden lg:flex items-center gap-2 text-xs">
                    <User className="w-3 h-3 text-gray-400" />
                    <span className="text-gray-300 max-w-[100px] truncate">{profile.full_name || profile.email?.split('@')[0]}</span>
                    {profile.subscription_status === 'paid' && (
                      <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">PRO</span>
                    )}
                    {profile.is_admin && (
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">ADMIN</span>
                    )}
                  </div>
                )}
                
                {/* PRO belongs to the self-service tools, not the website order flow. */}
                {isSelfServicePage && user && profile?.subscription_status !== 'paid' && (
                  <Link to="/upgrade" aria-label="Self-service PRO pricing">
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs px-3 py-1 h-8"
                    >
                      <Crown className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Self-service PRO</span>
                    </Button>
                  </Link>
                )}

                {/* Debug: Show current status */}
                {user && (
                  <span className="text-xs text-gray-500 hidden">
                    Status: {profile?.subscription_status || 'loading...'}
                  </span>
                )}
                
                <Button
                  onClick={handleAuthAction}
                  aria-label={user ? 'Sign out' : 'Sign in to self-service tools'}
                  title={user ? 'Sign out' : 'Sign in to self-service tools'}
                  variant="outline"
                  size="sm"
                  className="border-gray-700 text-gray-300 hover:bg-gray-800 text-xs px-3 py-1 h-8"
                >
                  {user ? (
                    <>
                      <LogOut className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Sign Out</span>
                    </>
                  ) : (
                    <>
                      <LogIn className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Tools sign-in</span>
                    </>
                  )}
                </Button>
              </>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              className="text-gray-400 hover:text-white"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </nav>
    </div>
  );
};

export default Navbar;